(() => {
  'use strict';

  class BravoPetProductFinder {
    constructor(root) {
      if (!root || root.dataset.bpFinderReady === 'true') return;

      this.root = root;
      this.root.dataset.bpFinderReady = 'true';

      this.busy = false;
      this.refreshing = false;
      this.pendingAction = null;
      this.lastSearch = window.location.search.slice(1);
      this.refreshTimer = null;
      this.fallbackTimer = null;

      this.onClick = this.onClick.bind(this);

      this.root.addEventListener('click', this.onClick);

      this.syncStepVisibility(false);
      this.observeDawnGrid();
    }

    /* =====================================================
       CONFIG
       ===================================================== */

    getSource(step) {
    return this.root.getAttribute(
    `data-step-${step}-source`
    ) || '';
    }

    getFinderSectionId() {
      return this.root.dataset.sectionId || '';
    }

    isStep3Enabled() {
    return this.root.getAttribute(
    'data-step-3-enabled'
    ) === 'true';
    }

    /* =====================================================
       CLICKS
       ===================================================== */

    onClick(event) {
      const option = event.target.closest('[data-bp-option]');

      if (option && this.root.contains(option)) {
        event.preventDefault();
        this.selectOption(option);
        return;
      }

      const back = event.target.closest('[data-bp-back]');

      if (back && this.root.contains(back)) {
        event.preventDefault();
        this.goBack(Number(back.dataset.bpBack));
        return;
      }

      const viewAll = event.target.closest('[data-bp-scroll-products]');

      if (viewAll && this.root.contains(viewAll)) {
        event.preventDefault();
        this.showAllProducts();
      }
    }

    /* =====================================================
       SELECT OPTION
       ===================================================== */

    selectOption(button) {
      if (this.busy) return;

      const step = Number(button.dataset.bpOptionStep);
      const param = button.dataset.bpParam;
      const value = button.dataset.bpValue;

      if (!step || !param || !value) return;

      const params = new URLSearchParams(window.location.search);

      /*
       * Changing one finder level must remove that level
       * and everything after it.
       */
      this.clearFinderFromStep(params, step);

      params.set(param, value);
      params.delete('page');

      this.applyThroughDawn(params, {
        scrollAfter: this.isFinalStep(step),
      });
    }

    /* =====================================================
       GO BACK
       ===================================================== */

    goBack(targetStep) {
      if (this.busy || !targetStep) return;

      const params = new URLSearchParams(window.location.search);

      /*
       * Back to Step 1:
       * remove Step 1 + Step 2 + Step 3.
       *
       * Back to Step 2:
       * preserve Step 1,
       * remove Step 2 + Step 3.
       */
      this.clearFinderFromStep(params, targetStep);

      params.delete('page');

      this.applyThroughDawn(params, {
        scrollAfter: false,
      });
    }

    /* =====================================================
       VIEW EVERYTHING
       ===================================================== */

    showAllProducts() {
      if (this.busy) return;

      const params = new URLSearchParams(window.location.search);

      let removedSomething = false;

      for (let step = 1; step <= 3; step += 1) {
        const source = this.getSource(step);

        if (source && params.has(source)) {
          params.delete(source);
          removedSomething = true;
        }
      }

      params.delete('page');

      if (!removedSomething) {
        this.scrollToProducts();
        return;
      }

      this.applyThroughDawn(params, {
        scrollAfter: true,
      });
    }

    /* =====================================================
       PARAMETER LOGIC
       ===================================================== */

    clearFinderFromStep(params, startingStep) {
      for (let step = startingStep; step <= 3; step += 1) {
        const source = this.getSource(step);

        if (source) {
          params.delete(source);
        }
      }
    }

    isFinalStep(step) {
      if (step === 3) return true;

      if (step === 2) {
        return !this.isStep3Enabled();
      }

      if (step === 1) {
        return !this.getSource(2);
      }

      return false;
    }

    /* =====================================================
       USE DAWN'S OWN FILTER ENGINE
       ===================================================== */

    applyThroughDawn(params, options = {}) {
      const searchParams = params.toString();

      /*
       * Dawn 16 already knows how to:
       *
       * - AJAX-render the collection
       * - update active filter pills
       * - update product counts
       * - update native filters
       * - update the URL
       *
       * Use that instead of duplicating Dawn.
       */
      if (
        typeof FacetFiltersForm === 'undefined' ||
        typeof FacetFiltersForm.renderPage !== 'function'
      ) {
        this.navigateNormally(params);
        return;
      }

      this.pendingAction = {
        scrollAfter: Boolean(options.scrollAfter),
        searchParams,
      };

      this.setBusy(true);

      clearTimeout(this.fallbackTimer);

      /*
       * Dawn renders asynchronously.
       * The ProductGrid observer below detects completion.
       */
      FacetFiltersForm.renderPage(
        searchParams,
        null,
        true
      );

      /*
       * Safety fallback only.
       * If Dawn somehow fails completely, normal Shopify
       * navigation still leaves the store usable.
       */
      this.fallbackTimer = window.setTimeout(() => {
        if (!this.pendingAction) return;

        console.warn(
          'BravoPet Finder: Dawn did not complete the AJAX update. Falling back to normal navigation.'
        );

        this.navigateNormally(
          new URLSearchParams(searchParams)
        );
      }, 8000);
    }

    /* =====================================================
       WATCH DAWN'S PRODUCT GRID
       ===================================================== */

    observeDawnGrid() {
      const grid = document.getElementById('ProductGridContainer');

      if (!grid) {
        console.warn(
          'BravoPet Finder: ProductGridContainer was not found.'
        );
        return;
      }

      this.gridObserver = new MutationObserver(() => {
        clearTimeout(this.refreshTimer);

        this.refreshTimer = window.setTimeout(() => {
          this.onDawnRenderComplete();
        }, 80);
      });

      this.gridObserver.observe(grid, {
        childList: true,
        subtree: true,
      });
    }

    async onDawnRenderComplete() {
      const currentSearch = window.location.search.slice(1);

      /*
       * Ignore unrelated DOM mutations when neither
       * the URL nor a finder action changed.
       */
      if (
        !this.pendingAction &&
        currentSearch === this.lastSearch
      ) {
        return;
      }

      this.lastSearch = currentSearch;

      try {
        await this.refreshFinderOnly(currentSearch);

        this.syncStepVisibility(true);

        if (this.pendingAction?.scrollAfter) {
          window.setTimeout(() => {
            this.scrollToProducts();
          }, 250);
        }
      } catch (error) {
        console.error(
          'BravoPet Finder could not refresh:',
          error
        );
      } finally {
        clearTimeout(this.fallbackTimer);

        this.pendingAction = null;
        this.setBusy(false);
      }
    }

    /* =====================================================
       REFRESH ONLY OUR FINDER SECTION
       ===================================================== */

    async refreshFinderOnly(searchParams) {
      if (this.refreshing) return;

      const sectionId = this.getFinderSectionId();

      if (!sectionId) {
        throw new Error(
          'Finder section ID is missing.'
        );
      }

      this.refreshing = true;

      try {
        const requestParams = new URLSearchParams(searchParams);

        requestParams.set('section_id', sectionId);

        const response = await fetch(
          `${window.location.pathname}?${requestParams.toString()}`,
          {
            method: 'GET',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Finder refresh returned HTTP ${response.status}.`
          );
        }

        const html = await response.text();

        const parsed = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const newFinder = parsed.querySelector(
          '[data-bp-finder]'
        );

        const newSteps = newFinder?.querySelector(
          '.bp-finder__steps'
        );

        const currentSteps = this.root.querySelector(
          '.bp-finder__steps'
        );

        if (!newFinder || !newSteps || !currentSteps) {
          throw new Error(
            'Refreshed finder markup could not be found.'
          );
        }

        currentSteps.innerHTML = newSteps.innerHTML;
      } finally {
        this.refreshing = false;
      }
    }

    /* =====================================================
       STEP VISIBILITY
       ===================================================== */

    syncStepVisibility(animate = true) {
      const step1 = this.root.querySelector(
        '[data-bp-step="1"]'
      );

      const step2 = this.root.querySelector(
        '[data-bp-step="2"]'
      );

      const step3 = this.root.querySelector(
        '[data-bp-step="3"]'
      );

      if (!step1) return;

      const step1Active = step1.querySelector(
        '[data-bp-option].is-active'
      );

      const step2Active = step2?.querySelector(
        '[data-bp-option].is-active'
      );

      let targetStep = 1;

      if (step1Active && step2) {
        targetStep = 2;
      }

      if (
        this.isStep3Enabled() &&
        step2Active &&
        step3
      ) {
        targetStep = 3;
      }

      const steps = this.root.querySelectorAll(
        '[data-bp-step]'
      );

      steps.forEach((step) => {
        const number = Number(step.dataset.bpStep);

        step.classList.remove(
          'is-entering',
          'is-leaving'
        );

        step.classList.toggle(
          'is-hidden',
          number !== targetStep
        );
      });

      const target = this.root.querySelector(
        `[data-bp-step="${targetStep}"]`
      );

      this.root.dataset.currentStep =
        String(targetStep);

      if (animate && target) {
        target.classList.add('is-entering');

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            target.classList.remove(
              'is-entering'
            );
          });
        });
      }
    }

    /* =====================================================
       LOADING
       ===================================================== */

    setBusy(state) {
      this.busy = state;

      this.root.classList.toggle(
        'is-loading',
        state
      );

      this.root.setAttribute(
        'aria-busy',
        state ? 'true' : 'false'
      );

      const loading = this.root.querySelector(
        '[data-bp-loading]'
      );

      if (loading) {
        loading.hidden = !state;
        loading.setAttribute(
          'aria-hidden',
          state ? 'false' : 'true'
        );
      }

      this.root
        .querySelectorAll(
          '[data-bp-option], [data-bp-back], [data-bp-scroll-products]'
        )
        .forEach((button) => {
          button.disabled = state;
        });
    }

    /* =====================================================
       SCROLL
       ===================================================== */

    scrollToProducts() {
      const grid = document.getElementById(
        'ProductGridContainer'
      );

      if (!grid) return;

      const headerOffset = 90;

      const top =
        grid.getBoundingClientRect().top +
        window.scrollY -
        headerOffset;

      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    }

    /* =====================================================
       FALLBACK
       ===================================================== */

    navigateNormally(params) {
      const search = params.toString();

      window.location.assign(
        window.location.pathname +
          (search ? `?${search}` : '')
      );
    }
  }

  /* =======================================================
     INITIALIZE
     ======================================================= */

  function initializeBravoPetFinders(scope = document) {
    if (
      scope instanceof Element &&
      scope.matches('[data-bp-finder]')
    ) {
      new BravoPetProductFinder(scope);
      return;
    }

    scope
      .querySelectorAll('[data-bp-finder]')
      .forEach((finder) => {
        new BravoPetProductFinder(finder);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initializeBravoPetFinders();
      }
    );
  } else {
    initializeBravoPetFinders();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeBravoPetFinders(event.target);
    }
  );
})();